import PageHero from "@/components/PageHero";
import Reveal, { Eyebrow } from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";
import { useLanguage } from "@/context/LanguageContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import { BUSINESS, IMAGES } from "@/data/site";
import { Phone, Mail, Instagram, MapPin, ArrowUpRight } from "lucide-react";
import { WhatsAppIcon } from "@/components/Icons";

export default function Contact() {
  const { t, lang } = useLanguage();
  usePageMeta(t.meta.contact.title, t.meta.contact.desc);
  const c = t.contactPage;

  const cards = [
    {
      label: c.callLabel,
      value: BUSINESS.phoneDisplay,
      href: BUSINESS.tel,
      Icon: Phone,
      id: "contact-call-now-link",
      external: false,
    },
    {
      label: c.waLabel,
      value: BUSINESS.phoneDisplay,
      href: BUSINESS.whatsapp,
      Icon: WhatsAppIcon,
      id: "contact-whatsapp-link",
      external: true,
    },
    {
      label: c.emailLabel,
      value: BUSINESS.email,
      href: BUSINESS.mailto,
      Icon: Mail,
      id: "contact-email-link",
      external: false,
    },
    {
      label: c.instaLabel,
      value: BUSINESS.instagramHandle,
      href: BUSINESS.instagram,
      Icon: Instagram,
      id: "contact-instagram-link",
      external: true,
    },
  ];

  return (
    <main>
      <PageHero eyebrow={c.eyebrow} title={c.heading} body={c.body} image={IMAGES.darkCup} />

      <section className="py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-2 lg:gap-20 lg:px-10">
          <div>
            <Reveal>
              <Eyebrow>{c.infoTitle}</Eyebrow>
            </Reveal>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {cards.map(({ label, value, href, Icon, id, external }, i) => (
                <Reveal key={id} delay={i * 0.08} className="h-full">
                  <a
                    href={href}
                    data-testid={id}
                    {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    className="group flex h-full flex-col justify-between border hairline bg-velvet/40 p-6 transition-all duration-500 hover:border-gold/45 hover:shadow-[0_0_36px_rgba(212,175,55,0.1)]"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 bg-gold/5 text-gold transition-all duration-500 group-hover:bg-gold group-hover:text-obsidian">
                      <Icon className="h-5 w-5" strokeWidth={1.5} />
                    </span>
                    <span className="mt-8">
                      <span className="block text-[11px] font-medium uppercase tracking-[0.18em] text-cream/50">
                        {label}
                      </span>
                      <span className="mt-1.5 flex items-center gap-2 font-serif text-xl text-cream transition-colors group-hover:text-gold-light">
                        {value}
                        <ArrowUpRight className="h-4 w-4 text-gold/70" strokeWidth={1.5} />
                      </span>
                    </span>
                  </a>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.15}>
              <div className="mt-10">
                <Eyebrow>{c.addressTitle}</Eyebrow>
                <address className="mt-5 flex items-start gap-3 text-base not-italic leading-relaxed text-cream/70">
                  <MapPin className="mt-1 h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
                  <span>
                    {BUSINESS.addressLines[lang].map((line) => (
                      <span key={line} className="block">
                        {line}
                      </span>
                    ))}
                  </span>
                </address>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-10">
                <Eyebrow>{c.mapTitle}</Eyebrow>
                <div
                  className="mt-5 aspect-[16/9] w-full overflow-hidden border hairline grayscale-[35%] invert-[0.92] hue-rotate-180"
                  data-testid="contact-map-embed"
                >
                  <iframe
                    title={c.mapTitle}
                    src={BUSINESS.mapEmbed}
                    className="h-full w-full"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <a
                  href={BUSINESS.mapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-open-maps-link"
                  className="mt-3 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-cream/60 transition-colors hover:text-gold"
                >
                  Google Maps
                  <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <EnquiryForm />
          </Reveal>
        </div>
      </section>
    </main>
  );
}
